import { SigneDirecteurItem } from './signeDirecteurPractice';

// Second Kama "Pratiquer" mode (App.tsx routes "Quiz" here, distinct from
// "Exercices" → signeDirecteurPractice.ts) — instead of naming the signe
// directeur within an already-correct spelling, this drills the reverse
// skill: given a word, pick which of four sign orderings is the real
// attested spelling. The three distractors per word are genuine
// permutations of the exact same, already-verified sign set (see
// signeDirecteurPractice.ts / lecture.ts / the kama-hieroglyph-decoder
// skill) — never invented signs, just reordered ones — generated with a
// fixed random seed so the position of the correct answer isn't always the
// same slot.
export const graphieReconstructionPractice: SigneDirecteurItem[] = [
  {
    key: 'nfr',
    word: {
      translation: 'Bon, beau, parfait',
      translationEn: 'Good, beautiful, perfect',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire nfr, « bon, beau, parfait ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write nfr, "good, beautiful, perfect." Which one is correct?',
        options: ['𓆑𓂋𓄤', '𓄤𓆑𓂋', '𓂋𓆑𓄤', '𓆑𓄤𓂋'],
        correctIndex: 1,
        optionsAreGlyphs: true,
        explanation: '𓄤𓆑𓂋 : le signe directeur (F35, nfr) vient toujours en premier, suivi de ses compléments phonétiques f (I9) et r (D21) qui confirment sa lecture — jamais dans un autre ordre (voir la fiche « nfr » dans Lire les hiéroglyphes).',
        explanationEn: '𓄤𓆑𓂋: the lead sign (F35, nfr) always comes first, followed by its phonetic complements f (I9) and r (D21) which confirm its reading — never in any other order (see the "nfr" entry in Lire les hiéroglyphes).',
      },
    },
  },
  {
    key: 'Msḫnt',
    word: {
      translation: 'Meskhenet – Déesse de la naissance',
      translationEn: 'Meskhenet – Goddess of birth',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire Msḫnt, « Meskhenet ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write Msḫnt, "Meskhenet." Which one is correct?',
        options: ['𓋴𓐍𓏏𓅆𓄟', '𓏏𓋴𓐍𓄟𓅆', '𓄟𓋴𓐍𓏏𓅆', '𓅆𓏏𓐍𓄟𓋴'],
        correctIndex: 2,
        optionsAreGlyphs: true,
        explanation: '𓄟𓋴𓐍𓏏𓅆 : le signe directeur F31 (ms) ouvre le mot, suivi des compléments phonétiques s, ḫ, t, puis du déterminatif final (une déesse assise) — jamais en tête ni au milieu.',
        explanationEn: '𓄟𓋴𓐍𓏏𓅆: the lead sign F31 (ms) opens the word, followed by the phonetic complements s, ḫ, t, then the final determinative (a seated goddess) — never at the start or in the middle.',
      },
    },
  },
  {
    key: 'Ỉmn',
    word: {
      translation: 'Amon – Dieu caché',
      translationEn: 'Amun – Hidden god',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire Ỉmn, « Amon ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write Ỉmn, "Amun." Which one is correct?',
        options: ['𓇋𓈖𓏠𓀭', '𓏠𓀭𓇋𓈖', '𓈖𓀭𓇋𓏠', '𓇋𓏠𓈖𓀭'],
        correctIndex: 3,
        optionsAreGlyphs: true,
        explanation: '𓇋𓏠𓈖𓀭 : ỉ ouvre le mot, puis le signe directeur (Y5, mn), puis son complément n, puis le déterminatif divin final.',
        explanationEn: '𓇋𓏠𓈖𓀭: ỉ opens the word, then the lead sign (Y5, mn), then its complement n, then the final divine determinative.',
      },
    },
  },
  {
    key: 'Nwt',
    word: {
      translation: 'Nout – Déesse du Ciel étoilé',
      translationEn: 'Nut – Goddess of the starry sky',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire Nwt, « Nout ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write Nwt, "Nut." Which one is correct?',
        options: ['𓏌𓏏𓇯𓁐', '𓁐𓇯𓏏𓏌', '𓇯𓁐𓏏𓏌', '𓁐𓏏𓏌𓇯'],
        correctIndex: 0,
        optionsAreGlyphs: true,
        explanation: '𓏌𓏏𓇯𓁐 : le signe directeur (W24, nw) ouvre le mot, suivi de t, du signe du ciel, puis du déterminatif de déesse.',
        explanationEn: '𓏌𓏏𓇯𓁐: the lead sign (W24, nw) opens the word, followed by t, the sky sign, then the goddess determinative.',
      },
    },
  },
  {
    key: 'hrw',
    word: {
      translation: 'Jour',
      translationEn: 'Day',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire hrw, « jour ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write hrw, "day." Which one is correct?',
        options: ['𓅱𓇳𓉔', '𓇳𓉔𓅱', '𓉔𓅱𓇳', '𓅱𓉔𓇳'],
        correctIndex: 2,
        optionsAreGlyphs: true,
        explanation: '𓉔𓅱𓇳 : les compléments phonétiques h et w précèdent, et le disque solaire N5 (le signe directeur qui porte le sens à lui seul) ferme le mot.',
        explanationEn: '𓉔𓅱𓇳: the phonetic complements h and w come first, and the sun disc N5 (the lead sign that carries the meaning on its own) closes the word.',
      },
    },
  },
  {
    key: 'Ḥr',
    word: {
      translation: 'Horus – Dieu faucon',
      translationEn: 'Horus – Falcon god',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire Ḥr, « Horus ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write Ḥr, "Horus." Which one is correct?',
        options: ['𓎛𓅆𓁷𓂋', '𓎛𓅆𓂋𓁷', '𓎛𓂋𓅆𓁷', '𓎛𓁷𓂋𓅆'],
        correctIndex: 3,
        optionsAreGlyphs: true,
        explanation: '𓎛𓁷𓂋𓅆 : ḥ ouvre le mot, puis le signe directeur (D2, un visage), puis r, puis le déterminatif divin (un faucon sur un support) qui ferme toujours le mot.',
        explanationEn: '𓎛𓁷𓂋𓅆: ḥ opens the word, then the lead sign (D2, a face), then r, then the divine determinative (a falcon on a standard) which always closes the word.',
      },
    },
  },
  {
    key: 'sbꜣ',
    word: {
      translation: 'Étoile',
      translationEn: 'Star',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire sbꜣ, « étoile ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write sbꜣ, "star." Which one is correct?',
        options: ['𓃀𓏺𓋴𓇼', '𓇼𓋴𓃀𓏺', '𓇼𓏺𓋴𓃀', '𓋴𓃀𓇼𓏺'],
        correctIndex: 3,
        optionsAreGlyphs: true,
        explanation: '𓋴𓃀𓇼𓏺 : s et b précèdent, puis le signe directeur (N14, une étoile) qui porte le sens, et un dernier trait de complément.',
        explanationEn: '𓋴𓃀𓇼𓏺: s and b come first, then the lead sign (N14, a star) which carries the meaning, and a final stroke complement.',
      },
    },
  },
  {
    key: 'Ỉtm',
    word: {
      translation: 'Atoum – Dieu du soleil couchant',
      translationEn: 'Atum – God of the setting sun',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire Ỉtm, « Atoum ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write Ỉtm, "Atum." Which one is correct?',
        options: ['𓇋𓍃𓅓𓀭', '𓅓𓇋𓀭𓍃', '𓀭𓍃𓇋𓅓', '𓍃𓀭𓇋𓅓'],
        correctIndex: 0,
        optionsAreGlyphs: true,
        explanation: '𓇋𓍃𓅓𓀭 : ỉ ouvre le mot, puis le signe directeur (U15, tm, un traîneau), puis m, puis le déterminatif divin final.',
        explanationEn: '𓇋𓍃𓅓𓀭: ỉ opens the word, then the lead sign (U15, tm, a sledge), then m, then the final divine determinative.',
      },
    },
  },
  {
    key: 'Ḏḥwtj',
    word: {
      translation: 'Thoth – Dieu de la sagesse',
      translationEn: 'Thoth – God of wisdom',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire Ḏḥwtj, « Thot ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write Ḏḥwtj, "Thoth." Which one is correct?',
        options: ['𓂧𓎛𓅝𓏏𓏭', '𓎛𓅝𓏏𓏭𓂧', '𓏭𓏏𓂧𓎛𓅝', '𓏭𓂧𓅝𓏏𓎛'],
        correctIndex: 0,
        optionsAreGlyphs: true,
        explanation: '𓂧𓎛𓅝𓏏𓏭 : ḏ et ḥ précèdent, puis le signe directeur (G26, un ibis), puis les compléments t et y qui referment le mot.',
        explanationEn: '𓂧𓎛𓅝𓏏𓏭: ḏ and ḥ come first, then the lead sign (G26, an ibis), then the complements t and y which close the word.',
      },
    },
  },
  {
    key: 'ṯꜣw',
    word: {
      translation: 'Air, vent, souffle',
      translationEn: 'Air, wind, breath',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire ṯꜣw, « le vent, le souffle ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write ṯꜣw, "wind, breath." Which one is correct?',
        options: ['𓊡𓅱𓅷', '𓅷𓊡𓅱', '𓅷𓅱𓊡', '𓅱𓊡𓅷'],
        correctIndex: 2,
        optionsAreGlyphs: true,
        explanation: '𓅷𓅱𓊡 : le signe directeur (P5, une voile gonflée par le vent) ouvre toujours le mot, suivi de w et du déterminatif final.',
        explanationEn: '𓅷𓅱𓊡: the lead sign (P5, a sail filled with wind) always opens the word, followed by w and the final determinative.',
      },
    },
  },
  {
    key: 'ḏḥw',
    word: {
      translation: 'Cigogne, grue',
      translationEn: 'Stork, crane',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire ḏḥw, « cigogne, grue ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write ḏḥw, "stork, crane." Which one is correct?',
        options: ['𓎛𓅝𓂧', '𓂧𓎛𓅝', '𓎛𓂧𓅝', '𓅝𓎛𓂧'],
        correctIndex: 1,
        optionsAreGlyphs: true,
        explanation: '𓂧𓎛𓅝 : ḏ et ḥ précèdent, et le signe directeur (G26, un ibis, la racine du nom de Thot) referme toujours le mot.',
        explanationEn: '𓂧𓎛𓅝: ḏ and ḥ come first, and the lead sign (G26, an ibis, the root of Thoth\'s name) always closes the word.',
      },
    },
  },
  {
    key: 'db',
    word: {
      translation: 'Hippopotame',
      translationEn: 'Hippopotamus',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire db, « hippopotame ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write db, "hippopotamus." Which one is correct?',
        options: ['𓃯𓄏𓃀', '𓃯𓃀𓄏', '𓃀𓃯𓄏', '𓄏𓃀𓃯'],
        correctIndex: 3,
        optionsAreGlyphs: true,
        explanation: '𓄏𓃀𓃯 : le signe directeur (F16, une corne) ouvre le mot, suivi de b et du déterminatif hippopotame final.',
        explanationEn: '𓄏𓃀𓃯: the lead sign (F16, a horn) opens the word, followed by b and the final hippopotamus determinative.',
      },
    },
  },
  {
    key: 'sw',
    word: {
      translation: 'Porc',
      translationEn: 'Pig',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire sw, « porc ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write sw, "pig." Which one is correct?',
        options: ['𓆄𓅱𓃟', '𓅱𓃟𓆄', '𓅱𓆄𓃟', '𓃟𓅱𓆄'],
        correctIndex: 0,
        optionsAreGlyphs: true,
        explanation: '𓆄𓅱𓃟 : le signe directeur (H6, une plume) ouvre toujours le mot, suivi de w et du déterminatif final.',
        explanationEn: '𓆄𓅱𓃟: the lead sign (H6, a feather) always opens the word, followed by w and the final determinative.',
      },
    },
  },
  {
    key: 'šspw',
    word: {
      translation: 'Sphinx',
      translationEn: 'Sphinx',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire šspw, « sphinx ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write šspw, "sphinx." Which one is correct?',
        options: ['𓅱𓊐𓊪', '𓊐𓊪𓅱', '𓊐𓅱𓊪', '𓊪𓅱𓊐'],
        correctIndex: 1,
        optionsAreGlyphs: true,
        explanation: '𓊐𓊪𓅱 : le signe directeur (O43, valeur šsp) ouvre le mot, suivi des compléments p et w.',
        explanationEn: '𓊐𓊪𓅱: the lead sign (O43, value šsp) opens the word, followed by the complements p and w.',
      },
    },
  },
  {
    key: 'rmn',
    word: {
      translation: 'Épaule',
      translationEn: 'Shoulder',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire rmn, « épaule ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write rmn, "shoulder." Which one is correct?',
        options: ['𓂋𓏠𓈖𓂢', '𓏠𓈖𓂢𓂋', '𓏠𓂢𓂋𓈖', '𓂢𓈖𓂋𓏠'],
        correctIndex: 0,
        optionsAreGlyphs: true,
        explanation: '𓂋𓏠𓈖𓂢 : r précède le signe directeur (Y5, mn), suivi de son complément n et du déterminatif final.',
        explanationEn: '𓂋𓏠𓈖𓂢: r precedes the lead sign (Y5, mn), followed by its complement n and the final determinative.',
      },
    },
  },
  {
    key: 'Ḏḥwty-ms',
    word: {
      translation: 'Thoutmosis',
      translationEn: 'Thutmose',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire Ḏḥwty-ms, « Thoutmosis ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write Ḏḥwty-ms, "Thutmose." Which one is correct?',
        options: ['𓋴𓄟𓅝', '𓄟𓋴𓅝', '𓋴𓅝𓄟', '𓅝𓄟𓋴'],
        correctIndex: 3,
        optionsAreGlyphs: true,
        explanation: '𓅝𓄟𓋴 : le signe directeur (G26, ibis, la racine de Thot) ouvre le mot, suivi de 𓄟 (F31, ms, « naître ») et de son complément s.',
        explanationEn: '𓅝𓄟𓋴: the lead sign (G26, ibis, the root of Thoth) opens the word, followed by 𓄟 (F31, ms, "to be born") and its complement s.',
      },
    },
  },
  {
    key: 'tpy',
    word: {
      translation: 'Premier, en chef',
      translationEn: 'First, foremost',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire tpy, « premier, en chef ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write tpy, "first, foremost." Which one is correct?',
        options: ['𓁶𓏭𓊪', '𓏭𓊪𓁶', '𓁶𓊪𓏭', '𓊪𓁶𓏭'],
        correctIndex: 2,
        optionsAreGlyphs: true,
        explanation: '𓁶𓊪𓏭 : le signe directeur (D1, une tête, valeur tp) ouvre le mot, suivi des compléments p et y.',
        explanationEn: '𓁶𓊪𓏭: the lead sign (D1, a head, value tp) opens the word, followed by the complements p and y.',
      },
    },
  },
  {
    key: 'Ḥrw',
    word: {
      translation: 'Partie supérieure, sommet',
      translationEn: 'Upper part, summit',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire Ḥrw, « partie supérieure, sommet ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write Ḥrw, "upper part, summit." Which one is correct?',
        options: ['𓇯𓂋𓁷', '𓇯𓁷𓂋', '𓂋𓇯𓁷', '𓁷𓂋𓇯'],
        correctIndex: 3,
        optionsAreGlyphs: true,
        explanation: '𓁷𓂋𓇯 : le signe directeur (D2, un visage) ouvre le mot, suivi de r et du déterminatif final.',
        explanationEn: '𓁷𓂋𓇯: the lead sign (D2, a face) opens the word, followed by r and the final determinative.',
      },
    },
  },
  {
    key: 'jmntt',
    word: {
      translation: 'Amenthès – Occident',
      translationEn: 'Amentet – The West',
      quiz: {
        question: 'Voici quatre façons possibles d\'écrire jmntt, « Amenthès, l\'Occident ». Laquelle est la bonne ?',
        questionEn: 'Here are four possible ways to write jmntt, "Amentet, the West." Which one is correct?',
        options: ['𓈉𓏏𓋀', '𓋀𓏏𓈉', '𓋀𓈉𓏏', '𓏏𓈉𓋀'],
        correctIndex: 1,
        optionsAreGlyphs: true,
        explanation: '𓋀𓏏𓈉 : le signe directeur (R14, idéogramme de « Ouest ») ouvre le mot, suivi de t et du déterminatif final.',
        explanationEn: '𓋀𓏏𓈉: the lead sign (R14, ideogram for "West") opens the word, followed by t and the final determinative.',
      },
    },
  },
];

export default graphieReconstructionPractice;
